/**
 * RoomTopics — anchor list of the room's questions (Step 11c, SPEC section 8 A).
 *
 * One topic per moderator message that started a turn (`metadata.turnId`), in send
 * order. No model call: the label is the message's first line. Each row shows the
 * round's top 3 emoji (pop and a soft pulse, at most once per 1.5s, when a reaction
 * lands live; never on a load or reconnect snapshot) and typing dots while the round runs. Click scrolls the thread to the
 * question and rings it; the row whose question is in view is marked current.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ChatTypingDots } from '@/components/chat/ChatTypingDots'
import { cn } from '@/lib/utils'
import { MENTION_TAG_TEXT_CLASS } from '@/components/chat/MentionHighlightTextarea'

import { ChatRoomMessageDto, ChatRoomReactionDto } from '@digital-twins/shared-types'

import { scrollToChatMessage } from './scrollToChatMessage'

export const TOPIC_PULSE_THROTTLE_MS = 1500
const MAX_TOPIC_EMOJI = 3
const DOCUMENT_BLOCK = /^\[Document: [^\]]*\][\s\S]*?\n\[\/Document\]\n*/

export interface RoomTopic {
  messageId: string
  turnId: string
  label: string
  /** 'image' | 'video' when the message has only attachments. */
  attachmentKind: 'image' | 'video' | null
}

export interface RoomTopicActivity {
  topEmoji: Array<string>
  reactionCount: number
}

/** First line of the user's own text (an inlined document block is skipped). */
export function topicLabel(content: string): string {
  const own = content.replace(DOCUMENT_BLOCK, '').trim()
  return own.split('\n').find((line) => line.trim().length > 0)?.trim() ?? ''
}

/** One topic per moderator message carrying `metadata.turnId`, send order. */
export function buildRoomTopics(messages: Array<ChatRoomMessageDto>): Array<RoomTopic> {
  const topics: Array<RoomTopic> = []
  const ordered = [...messages].sort((a, b) => a.sequenceIndex - b.sequenceIndex)
  for (const message of ordered) {
    if (message.senderType !== 'user') {
      continue
    }
    const turnId = (message.metadata as Record<string, unknown> | null)?.turnId
    if (typeof turnId !== 'string' || !turnId) {
      continue
    }
    const label = topicLabel(message.content ?? '')
    const hasImage = Boolean(message.imageKeys?.length || message.images?.length)
    const hasVideo = Boolean(message.videoRefs?.length)
    topics.push({
      messageId: message.id,
      turnId,
      label,
      attachmentKind: label ? null : hasVideo && !hasImage ? 'video' : hasImage ? 'image' : null,
    })
  }
  return topics
}

/** Top emoji by count (then first arrival) and the reaction count, per turn. */
export function buildTopicActivity(
  reactions: Array<ChatRoomReactionDto>,
): Record<string, RoomTopicActivity> {
  const perTurn = new Map<string, Map<string, { count: number; firstAt: string }>>()
  for (const reaction of reactions) {
    const byEmoji = perTurn.get(reaction.turnId) ?? new Map()
    const entry = byEmoji.get(reaction.emoji)
    if (entry) {
      entry.count += 1
      if (reaction.at < entry.firstAt) {
        entry.firstAt = reaction.at
      }
    } else {
      byEmoji.set(reaction.emoji, { count: 1, firstAt: reaction.at })
    }
    perTurn.set(reaction.turnId, byEmoji)
  }
  const out: Record<string, RoomTopicActivity> = {}
  for (const [turnId, byEmoji] of perTurn) {
    const sorted = [...byEmoji.entries()].sort(
      (a, b) => b[1].count - a[1].count || a[1].firstAt.localeCompare(b[1].firstAt),
    )
    out[turnId] = {
      topEmoji: sorted.slice(0, MAX_TOPIC_EMOJI).map(([emoji]) => emoji),
      reactionCount: sorted.reduce((sum, [, entry]) => sum + entry.count, 0),
    }
  }
  return out
}

const NO_LIVE_REACTIONS: ReadonlySet<string> = new Set()

/** Same identity as useChatRoom's `reactionKey` (and ReactionChips). */
function reactionLiveKey(reaction: ChatRoomReactionDto): string {
  return `${reaction.messageSequenceIndex}|${reaction.by}|${reaction.emoji}`
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

interface RoomTopicsProps {
  messages: Array<ChatRoomMessageDto>
  reactions?: Array<ChatRoomReactionDto>
  /** `${messageSequenceIndex}|${by}|${emoji}` of reactions that arrived live; only they pulse a row. */
  liveReactionKeys?: ReadonlySet<string>
  /** A round is running: the newest topic shows typing dots. */
  isRoundRunning?: boolean
  /** Runs before a scroll (the phone sheet closes first). */
  onBeforeNavigate?: () => void
  /** Loads more history when the question is not rendered. */
  onBackfill?: () => void | Promise<void>
}

export function RoomTopics(props: RoomTopicsProps) {
  const {
    messages,
    reactions = [],
    liveReactionKeys = NO_LIVE_REACTIONS,
    isRoundRunning = false,
    onBeforeNavigate,
    onBackfill,
  } = props

  const { t } = useTranslation('app')
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null)
  const [pulse, setPulse] = useState<{ turnId: string; emoji: string; nonce: number } | null>(null)
  const lastPulseAtRef = useRef(new Map<string, number>())
  const listRef = useRef<HTMLDivElement>(null)

  const topics = useMemo(() => buildRoomTopics(messages), [messages])
  const activity = useMemo(() => buildTopicActivity(reactions), [reactions])

  // Pulse a row only for reactions that arrived live (`liveReactionKeys`); a load or
  // reconnect snapshot never pulses, whatever it adds.
  useEffect(() => {
    if (liveReactionKeys.size === 0 || prefersReducedMotion()) {
      return
    }
    const newestLiveByTurn = new Map<string, ChatRoomReactionDto>()
    for (const reaction of reactions) {
      if (!liveReactionKeys.has(reactionLiveKey(reaction))) {
        continue
      }
      const newest = newestLiveByTurn.get(reaction.turnId)
      if (!newest || reaction.at.localeCompare(newest.at) > 0) {
        newestLiveByTurn.set(reaction.turnId, reaction)
      }
    }
    for (const [turnId, newest] of newestLiveByTurn) {
      const now = Date.now()
      const lastAt = lastPulseAtRef.current.get(turnId) ?? 0
      if (now - lastAt < TOPIC_PULSE_THROTTLE_MS) {
        continue
      }
      lastPulseAtRef.current.set(turnId, now)
      setPulse({ turnId, emoji: newest.emoji, nonce: now })
    }
  }, [liveReactionKeys, reactions])

  useEffect(() => {
    if (!pulse) {
      return
    }
    const timer = window.setTimeout(() => setPulse(null), TOPIC_PULSE_THROTTLE_MS)
    return () => window.clearTimeout(timer)
  }, [pulse])

  // Keep the newest topic visible as topics are appended.
  const topicCount = topics.length
  useEffect(() => {
    const list = listRef.current
    if (list) {
      list.scrollTop = list.scrollHeight
    }
  }, [topicCount])

  // Current topic: the last question in view.
  const topicIdsKey = topics.map((topic) => topic.messageId).join(',')
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || topics.length === 0) {
      return
    }
    const visible = new Set<string>()
    const order = topics.map((topic) => topic.messageId)
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const messageId = (entry.target as HTMLElement).dataset.testid?.replace(/^message-/, '')
        if (!messageId) {
          continue
        }
        if (entry.isIntersecting) {
          visible.add(messageId)
        } else {
          visible.delete(messageId)
        }
      }
      const inView = order.filter((id) => visible.has(id))
      if (inView.length > 0) {
        setCurrentMessageId(inView[inView.length - 1])
      }
    })
    for (const messageId of order) {
      const element = document.querySelector(`[data-testid="message-${CSS.escape(messageId)}"]`)
      if (element) {
        observer.observe(element)
      }
    }
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicIdsKey])

  if (topics.length === 0) {
    return null
  }

  const onSelect = (topic: RoomTopic) => {
    onBeforeNavigate?.()
    setCurrentMessageId(topic.messageId)
    void scrollToChatMessage(topic.messageId, { onBackfill })
  }

  return (
    <TooltipProvider delayDuration={400}>
      <nav aria-label={t('roomPane.topics')}>
        <div ref={listRef} className="flex max-h-[216px] flex-col gap-0.5 overflow-y-auto">
          {topics.map((topic, index) => {
            const label =
              topic.label ||
              (topic.attachmentKind === 'video' ? t('roomPane.topicVideo') : t('roomPane.topicImage'))
            const rowActivity = activity[topic.turnId]
            const isCurrent = currentMessageId === topic.messageId
            const isPulsing = pulse?.turnId === topic.turnId
            const isTyping = isRoundRunning && index === topics.length - 1
            return (
              <Tooltip key={topic.messageId}>
                <TooltipTrigger
                  type="button"
                  data-testid={`room-topic-${index}`}
                  aria-current={isCurrent ? 'true' : undefined}
                  aria-label={t('roomPane.topicAria', {
                    label,
                    count: rowActivity?.reactionCount ?? 0,
                  })}
                  onClick={() => onSelect(topic)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md border-l-2 px-2 py-1.5 text-left text-sm text-twin-chat-fg transition-colors duration-300',
                    'hover:bg-twin-chat-bubble/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
                    isCurrent ? 'border-twin-chat-accent bg-twin-chat-bubble/30' : 'border-transparent',
                    isPulsing && 'bg-twin-chat-accent/[0.12]',
                  )}
                >
                  <span className={cn('min-w-0 flex-1 truncate', !topic.label && 'text-twin-chat-muted')}>
                    <TopicLabelText text={label} />
                  </span>
                  {rowActivity && rowActivity.topEmoji.length > 0 && (
                    <span aria-hidden="true" className="flex shrink-0 items-center gap-0.5 text-sm leading-none">
                      {rowActivity.topEmoji.map((emoji) => (
                        <span
                          key={emoji}
                          className={cn(
                            isPulsing &&
                              pulse?.emoji === emoji &&
                              'motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-300',
                          )}
                        >
                          {emoji}
                        </span>
                      ))}
                    </span>
                  )}
                  {isTyping && (
                    <span aria-hidden="true" className="inline-flex shrink-0 origin-right scale-75" data-testid={`room-topic-${index}-typing`}>
                      <ChatTypingDots />
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[280px] whitespace-pre-wrap">
                  {label}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      </nav>
    </TooltipProvider>
  )
}

/** Mentions in the tag color, like the bubble. */
function TopicLabelText(props: { text: string }) {
  const { text } = props

  const parts = text.split(/(@\w+)/g)

  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('@') ? (
          <span key={index} className={MENTION_TAG_TEXT_CLASS}>{part}</span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  )
}
